// Small helpers for the account pages (change email / phone, billing details, receipts).
const API = process.env.REACT_APP_BACKEND_URL;
export const TOKEN_KEY = 'rideburaq_token';

export const authToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
};

// The server's answer can be {detail: "text"}, {detail: {code, message}} or a list of field errors.
export const messageFrom = (body, fallback) => {
  const d = body && body.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d.message === 'string') return d.message;
  if (Array.isArray(d) && d[0] && d[0].msg) return d[0].msg;
  return fallback;
};

export const codeFrom = (body) => (body && body.detail && body.detail.code) || '';

// JSON request with the signed-in user's token. Resolves { ok, status, body }.
export const apiJson = async (path, method = 'GET', payload = undefined) => {
  const token = authToken();
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
};

// Download a paid ride's PDF receipt (needs the signed-in token, so it is fetched and saved as a file).
export const downloadReceiptPdf = async (reference) => {
  const token = authToken();
  const res = await fetch(`${API}/api/rides/${encodeURIComponent(reference)}/receipt.pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(messageFrom(body, 'There is no receipt for this ride yet.'));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `RideBuraq-receipt-${reference}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// Rate a finished ride. Resolves the saved rating; throws an Error with the server's message otherwise.
export const RATING_TAGS = ['Spotless car', 'Right on time', 'Smooth ride', 'Professional', 'Felt safe', 'Great conversation'];

export const rateRide = async (reference, { stars, tags = [], note = '' }) => {
  const payload = { stars, tags: tags.filter((t) => RATING_TAGS.includes(t)) };
  const text = (note || '').trim().slice(0, 1000);
  if (text) payload.note = text;
  const r = await apiJson(`/api/rides/${encodeURIComponent(reference)}/rating`, 'POST', payload);
  if (!r.ok) throw new Error(messageFrom(r.body, 'We could not save your rating. Please try again.'));
  return r.body.rating;
};

// ---- Cancelling a ride: the SERVER says what it costs (never calculated here) ----
const CLOSED = ['completed', 'cancelled', 'in-progress', 'in_progress', 'on_trip'];
export const canTryCancel = (ride) => !!ride && !CLOSED.includes(String(ride.status || '').toLowerCase());

export const previewCancel = async (reference) => {
  const r = await apiJson(`/api/rides/${encodeURIComponent(reference)}/cancel-preview`);
  if (!r.ok) throw new Error(messageFrom(r.body, 'We could not check the cancellation cost. Please try again.'));
  return r.body;
};

export const cancelRide = async (reference) => {
  const r = await apiJson(`/api/rides/${encodeURIComponent(reference)}/cancel`, 'POST', {});
  if (!r.ok) throw new Error(messageFrom(r.body, 'We could not cancel this ride. Please try again.'));
  return r.body;
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
// The line shown after cancelling, from the server's own numbers.
export const cancelResultLine = (preview, result) => {
  const fee = Number(result?.cancellation_fee ?? preview?.fee ?? 0);
  const refund = Number(preview?.refund ?? 0);
  if (fee > 0) return refund > 0 ? `Ride cancelled. ${money(fee)} was kept and ${money(refund)} is being refunded.` : `Ride cancelled. The cancellation fee of ${money(fee)} was kept.`;
  const paid = Number(preview?.paid ?? 0);
  return paid > 0 ? `Ride cancelled. ${money(paid)} is being refunded to your card.` : 'Ride cancelled. No charge.';
};
